import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { NotificationIcon } from "components/icons";
import { INBOX_V2_FEATURE } from "constants/feature-flags";
import useIsMobile from "hooks/use-is-mobile.hook";
import useNotifications from "hooks/use-notifications";
import { NotificationTypes } from "services/types/notifications";

import NotificationItemV2 from "./notification-item/notification-item-v2.component";
import NotificationTab from "./notification-tab/notification-tab.component";

export default function NotificationsMenu() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  const flags = useFlags();
  const isInboxV2Enabled = flags[INBOX_V2_FEATURE];

  const {
    notifications,
    deleteNotification,
    activeType,
    readNotification,
    setActiveType,
    unreadNotificationsCount,
    handleReadAllNotifications,
    refetch,
    loadMore,
    hasMore,
    isLoadingMore
  } = useNotifications();

  const onOpen = useCallback(() => {
    refetch();
    setIsOpen(true);
  }, [refetch]);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const filteredNotifications = notifications;

  return (
    <>
      <Box component="span" mr={isMobile ? -1 : 0}>
        <IconButton onClick={onOpen} data-testid="notifications-button">
          <Badge
            color="primary"
            data-testid="unread-notifications"
            badgeContent={unreadNotificationsCount.all}
            sx={{ zIndex: 0 }}
          >
            <NotificationIcon />
          </Badge>
        </IconButton>
      </Box>

      <Drawer
        PaperProps={{
          sx: {
            width: 630
          }
        }}
        variant="persistent"
        anchor="right"
        sx={{
          zIndex: theme.zIndex.snackbar + 1
        }}
        open={isOpen}
        onClose={onClose}
      >
        {isOpen && (
          <>
            <Box
              display="flex"
              justifyContent="end"
              position="absolute"
              width="100%"
            >
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box padding="32px 16px 16px">
              <Stack
                justifyContent="center"
                sx={{ width: "100%", height: "100%" }}
                spacing={1}
              >
                <Tabs
                  centered
                  variant="fullWidth"
                  value={activeType}
                  onChange={(_, value) => setActiveType(value)}
                  sx={{
                    overflow: "visible",
                    "& > div": { overflow: "visible !important" }
                  }}
                >
                  <Tab
                    value={null}
                    label={
                      <NotificationTab count={unreadNotificationsCount.all}>
                        All
                      </NotificationTab>
                    }
                    sx={tabSx}
                  />

                  {isInboxV2Enabled && (
                    <Tab
                      value={NotificationTypes.MESSAGE}
                      label={
                        <NotificationTab
                          count={
                            unreadNotificationsCount[NotificationTypes.MESSAGE]
                          }
                        >
                          Assigned Messages
                        </NotificationTab>
                      }
                      sx={tabSx}
                    />
                  )}

                  <Tab
                    value={NotificationTypes.REPORT}
                    label={
                      <NotificationTab
                        count={
                          unreadNotificationsCount[NotificationTypes.REPORT]
                        }
                      >
                        Reports
                      </NotificationTab>
                    }
                    sx={tabSx}
                  />

                  <Tab
                    value={NotificationTypes.INTAKE_FORM}
                    label={
                      <NotificationTab
                        count={
                          unreadNotificationsCount[
                            NotificationTypes.INTAKE_FORM
                          ]
                        }
                      >
                        Onboarding Forms
                      </NotificationTab>
                    }
                    sx={tabSx}
                  />
                </Tabs>
              </Stack>
            </Box>
            <List
              sx={{
                display: "flex",
                flexDirection: "column",
                px: 2,
                gap: 1,
                overflowY: "auto"
              }}
            >
              {filteredNotifications.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    boxSizing: "border-box",
                    padding: 2
                  }}
                >
                  <Typography color="GrayText">Empty notifications</Typography>
                </Box>
              )}

              {filteredNotifications.map((notification) => {
                return (
                  <NotificationItemV2
                    key={notification.id}
                    notification={notification}
                    onDelete={deleteNotification}
                    readNotification={readNotification}
                  />
                );
              })}

              {hasMore && (
                <Box display="flex" justifyContent="center" py={2}>
                  <Button
                    size="small"
                    onClick={loadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? "Loading..." : "Load more"}
                  </Button>
                </Box>
              )}
            </List>

            {notifications.length > 0 && (
              <Button
                size="small"
                onClick={handleReadAllNotifications}
                sx={{ marginY: 1 }}
              >
                Read all notifications
              </Button>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}

const tabSx = {
  fontSize: "14px",
  padding: 0,
  overflow: "visible"
};
